import { ButtonTabs, FoodsIcon, IconMapping, Typography } from "@foodpilot/foods";
import { Stack, useTheme } from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { URLHandler } from "src/services/URLHandler";
import { MissingQuestionnaire } from "src/components/SSQ/MissingQuestionnaire";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useProductScoresContext } from "src/context/Product/Scores/useProductScores";

type ProductQuestionnaireLink = {
    to: string;
    label: string;
    icon: keyof IconMapping;
};

export const ProductQuestionnaire = () => {
    const { company } = useCompanyMetadata();
    const { product, ssqVersion, activeCompanyId } = useProductMetadataContext();
    const { productSummary } = useProductScoresContext();
    const [navigate, location] = [useNavigate(), useLocation()];
    const theme = useTheme();

    const path = location.pathname;

    // Extraire l'URL de retour depuis les paramètres pour la propager
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");

    // Déterminer si c'est un produit intermédiaire pour utiliser les bonnes URLs
    const isIntermediateProduct = product.isIntermediate === true;
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;

    const productQuestionnaireLinks: ProductQuestionnaireLink[] = [
        {
            to:
                returnUrl ?
                    `${urlHandlerRoutes.ssqSummary(company.id, product.id)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   urlHandlerRoutes.ssqSummary(company.id, product.id),
            label: t("questionnaire.navigation.summary"),
            icon: "questionnaires",
        },
        {
            to:
                returnUrl ?
                    `${urlHandlerRoutes.ssqProduct(company.id, product.id, activeCompanyId)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   urlHandlerRoutes.ssqProduct(company.id, product.id, activeCompanyId),
            label: t("questionnaire.navigation.product"),
            icon: "products",
        },
    ];

    if (productSummary && productSummary.ingredients !== null) {
        productQuestionnaireLinks.push({
            to:
                returnUrl ?
                    `${urlHandlerRoutes.ssqIngredients(company.id, product.id)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   urlHandlerRoutes.ssqIngredients(company.id, product.id),
            label: t("questionnaire.navigation.ingredients"),
            icon: "ingredients",
        });
    }

    if (productSummary && productSummary.packagings !== null) {
        productQuestionnaireLinks.push({
            to:
                returnUrl ?
                    `${urlHandlerRoutes.ssqPackagings(company.id, product.id)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   urlHandlerRoutes.ssqPackagings(company.id, product.id),
            label: t("questionnaire.navigation.packagings", "Packagings"),
            icon: "packaging",
        });
    }

    const computeNavigationIndex = () => {
        return productQuestionnaireLinks.findIndex((link) => {
            const linkWithoutParams = link.to.replace(/\?.*$/, "");

            return path.endsWith(linkWithoutParams) || 0;
        });
    };

    const [defaultNavigationIndex, setDefaultNavigationIndex] = useState(0);

    useEffect(() => {
        setDefaultNavigationIndex(computeNavigationIndex());
    }, [computeNavigationIndex]);

    if (ssqVersion === undefined) {
        return <MissingQuestionnaire label={t("product.no_questionnaire_info")} />;
    }
    if (ssqVersion.ssq.monolithic === true) {
        return (
            <Stack paddingTop={theme.spacing(3)}>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_BEFORE} />
                <Outlet />
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_AFTER} />
            </Stack>
        );
    }

    return (
        <Stack padding={theme.spacing(2, 0)} gap={theme.spacing(2)}>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_QUESTIONNAIRE_HEADER} />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_BEFORE} />
            <ButtonTabs
                key={`product-questionnaire-navigation-${defaultNavigationIndex}`}
                choices={productQuestionnaireLinks.map((link, index) => (
                    <Stack
                        direction={"row"}
                        gap={1}
                        alignItems={"center"}
                        key={`product-questionnaire-navigation-link-${index + 1}`}
                    >
                        <FoodsIcon icon={link.icon} size={3} />
                        <Typography variant="h5" color={"inherit"}>
                            {link.label}
                        </Typography>
                    </Stack>
                ))}
                onClick={(index) => {
                    const selectedLink = productQuestionnaireLinks[index];
                    navigate(selectedLink.to);
                }}
                defaultIndex={defaultNavigationIndex}
            />
            <Stack>
                <Outlet />
            </Stack>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_AFTER} />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_QUESTIONNAIRE_FOOTER} />
        </Stack>
    );
};
